import { makeStyles, Typography } from "@material-ui/core";
import { AvatarGroup, useAvatarGroupItems } from "@resource/atlas";
import cx from "clsx";
import DOMPurify from "isomorphic-dompurify";
import _ from "lodash";
import moment from "moment";
import React, { useState } from "react";
import useAnalytics from "react-hooks/useAnalytics";
import { ResourceCustomTheme } from "styles/config";
import { Event as EventType } from "types/legacy";

import ProminentInstructionsCard from "../../shared/ProminentInstructionsCard";
import EventModal from "./EventModal";

const useStyles = makeStyles((_theme: ResourceCustomTheme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    marginTop: 5,
    padding: 16,
    border: "solid 1px #f1f1f1",
    borderRadius: 8,
    cursor: "pointer",
    "&:hover": {
      border: "solid 1px rgb(204, 204, 204)",
    },
  },
  timeAndInterviwers: {
    display: "flex",
    justifyContent: "space-between",
  },
  timeAndTitle: {
    display: "flex",
    flexDirection: "column",
  },
  title: {
    overflowWrap: "anywhere",
  },
  time: {
    lineHeight: "16px",
  },
  titleAndIcon: {
    display: "flex",
    alignItems: "center",
  },
  titleIcon: {
    marginTop: 4,
    marginLeft: 5,
    height: 10,
  },
  interviewers: {
    display: "flex",
    alignItems: "center",
    marginLeft: 16,
    paddingLeft: 16,
  },
  interviewerAvatar: {
    position: "relative",
    marginLeft: "-15px",
    border: "solid 2px white",
  },
  disabled: {
    opacity: 0.4,
    "&:hover": {
      border: "solid 1px #f1f1f1",
    },
  },
}));

type EventProps = {
  event: EventType;
  showInterviewers?: boolean;
  unifiedConferenceLink?: string;
};

function Event({
  event,
  showInterviewers = true,
  unifiedConferenceLink,
}: EventProps) {
  const { title, startTime, endTime, prominentInstructions, interviewers } =
    event;

  const [openDialog, setOpenDialog] = useState(false);
  const classes = useStyles();
  const analytics = useAnalytics();
  const startTimeText = moment(startTime).tz(moment.tz.guess()).format("h:mm");
  const endTimeText = moment(endTime).tz(moment.tz.guess()).format("h:mm a z");
  const eventInPast = moment(endTime).isBefore(moment());
  // We don't show HTML in the card title
  const sanitizedTitle = DOMPurify.sanitize(title, { ALLOWED_TAGS: [] });
  const finalProminentInstructions = _.reject(prominentInstructions, {
    url: unifiedConferenceLink,
  });

  const interviewerAvatarGroupItems = useAvatarGroupItems(
    (i) =>
      showInterviewers
        ? interviewers.map((interviewer) =>
            i.avatar({
              key: interviewer.id,
              initials: `${interviewer?.firstName
                ?.slice(0, 1)
                .toUpperCase()}${interviewer?.lastName
                ?.slice(0, 1)
                .toUpperCase()}`,
              name: `${interviewer?.firstName} ${interviewer?.lastName}`,
              image: interviewer?.imageUrl,
            })
          )
        : [],
    [interviewers, showInterviewers]
  );

  return (
    <>
      <div
        tabIndex={0}
        role="button"
        className={cx(classes.root, {
          [classes.disabled]: eventInPast,
        })}
        onClick={() => {
          setOpenDialog(true);
          analytics.track("Event Clicked", {
            eventTitle: title,
          });
        }}
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === "Enter") {
            setOpenDialog(true);
            analytics.track("Event Clicked", {
              eventTitle: title,
            });
          }
        }}
      >
        <div className={classes.timeAndInterviwers}>
          <div className={classes.timeAndTitle}>
            <div className={classes.titleAndIcon}>
              <Typography
                dangerouslySetInnerHTML={{
                  __html: sanitizedTitle,
                }}
                variant="body1"
                className={classes.title}
              />
            </div>
            <Typography variant="subtitle1">
              {startTimeText} - {endTimeText}
            </Typography>
          </div>
          {showInterviewers && (
            <div className={classes.interviewers}>
              <AvatarGroup items={interviewerAvatarGroupItems} />
            </div>
          )}
        </div>
        {!eventInPast && (
          <div className="flex flex-col gap-y-3 mt-4">
            {_.map(finalProminentInstructions, (i) => (
              <ProminentInstructionsCard
                key={i.id}
                label={i.label}
                url={i.url}
              />
            ))}
          </div>
        )}
      </div>
      <EventModal
        open={openDialog}
        event={event}
        onClose={() => setOpenDialog(false)}
      />
    </>
  );
}
export default Event;
